export const TIMELINE_EVENT_LAYOUT = Object.freeze({
  EVENT_WIDTH: 200,
  EVENT_HEIGHT: 60,
  COMPACT_EVENT_HEIGHT: 42,
  OVERFLOW_MARKER_HEIGHT: 36,
  POINT_LANE_SPACING: 78,
  RANGE_BAND_DISTANCE: 34,
  RANGE_SPAN_HEIGHT: 54,
  RANGE_LABEL_TOP: 9,
  RANGE_ROW_GAP: 3,
  RANGE_ROW_SPACING: 57,
  BASE_DISTANCE: 100,
  MIN_RANGE_LABEL_WIDTH: 118,
  RANGE_LABEL_VIEWPORT_PADDING: 12,
  EVENT_PADDING: 22,
  VIEWPORT_EDGE_PADDING: 18,
  OVERFLOW_MARKER_EDGE_PADDING: 48,
  OVERFLOW_BUTTON_WIDTH: 104
});

const getEventImageUrl = (event) => {
  return event?.imageUrl || event?.imageURL || event?.image || '';
};

const defaultEnsureDate = (dateInput) => {
  if (dateInput instanceof Date) return dateInput;
  if (typeof dateInput === 'string' || typeof dateInput === 'number') {
    const date = new Date(dateInput);
    if (!Number.isNaN(date.getTime())) return date;
  }
  return null;
};

export const calculateEventOffsets = ({
  events,
  currentScale,
  currentStartTime,
  viewportWidth,
  viewportHeight,
  verticalSpace,
  ensureDate = defaultEnsureDate,
  config = TIMELINE_EVENT_LAYOUT
}) => {
  const offsets = new Map();
  const overflowGroups = [];
  const visibleBottomPointRects = [];
  const rangeEvents = events.filter(event => event.type === 'range' && event.endDate);
  const pointEvents = events.filter(event => event.type !== 'range' || !event.endDate);
  const topEvents = pointEvents.filter(event => (event.position || 'top') === 'top');
  const bottomEvents = pointEvents.filter(event => event.position === 'bottom');

  const {
    EVENT_WIDTH,
    EVENT_HEIGHT,
    COMPACT_EVENT_HEIGHT,
    OVERFLOW_MARKER_HEIGHT,
    POINT_LANE_SPACING,
    RANGE_BAND_DISTANCE,
    RANGE_SPAN_HEIGHT,
    RANGE_ROW_SPACING,
    BASE_DISTANCE,
    MIN_RANGE_LABEL_WIDTH,
    RANGE_LABEL_VIEWPORT_PADDING,
    EVENT_PADDING,
    VIEWPORT_EDGE_PADDING,
    OVERFLOW_MARKER_EDGE_PADDING,
    OVERFLOW_BUTTON_WIDTH
  } = config;
  const fallbackHalfHeight = Math.max(0, (viewportHeight || 700) / 2);

  const getVisualInterval = (event) => {
    const eventDate = ensureDate(event.date)?.getTime();
    if (!Number.isFinite(eventDate)) return null;

    const startPosition = (eventDate - currentStartTime) * currentScale;

    return {
      start: startPosition - (EVENT_WIDTH / 2) - (EVENT_PADDING / 2),
      end: startPosition + (EVENT_WIDTH / 2) + (EVENT_PADDING / 2)
    };
  };

  const getRangeMetrics = (event) => {
    const eventDate = ensureDate(event.date)?.getTime();
    const eventEndDate = ensureDate(event.endDate)?.getTime();
    if (!Number.isFinite(eventDate) || !Number.isFinite(eventEndDate)) return null;

    const startPosition = (eventDate - currentStartTime) * currentScale;
    const endPosition = (eventEndDate - currentStartTime) * currentScale;
    const rawRangeLeft = Math.min(startPosition, endPosition);
    const rawRangeRight = Math.max(startPosition, endPosition);
    const rawRangeWidth = rawRangeRight - rawRangeLeft;
    const labelWidth = Math.min(260, Math.max(MIN_RANGE_LABEL_WIDTH, String(event.title || '').length * 7.5 + 32));
    const rangeMidpoint = rawRangeLeft + (rawRangeWidth / 2);
    const minLabelCenter = Math.min(
      (labelWidth / 2) + RANGE_LABEL_VIEWPORT_PADDING,
      viewportWidth / 2
    );
    const maxLabelCenter = Math.max(
      viewportWidth - (labelWidth / 2) - RANGE_LABEL_VIEWPORT_PADDING,
      viewportWidth / 2
    );
    const labelCenter = Math.min(Math.max(rangeMidpoint, minLabelCenter), maxLabelCenter);
    const labelLeft = labelCenter - (labelWidth / 2);
    const labelRight = labelLeft + labelWidth;
    const collisionLeft = Math.min(rawRangeLeft, labelLeft);
    const collisionRight = Math.max(rawRangeRight, labelRight);

    return {
      rangeLeft: rawRangeLeft,
      rangeRight: rawRangeRight,
      rangeWidth: rawRangeWidth,
      interval: {
        start: collisionLeft - (EVENT_PADDING / 2),
        end: collisionRight + (EVENT_PADDING / 2)
      }
    };
  };

  const getCollisionGroupIds = (sideEvents) => {
    const compactEventIds = new Set();
    const eventIntervals = sideEvents
      .map(event => ({ event, interval: getVisualInterval(event) }))
      .filter(({ interval }) => Boolean(interval));

    for (let i = 0; i < eventIntervals.length; i += 1) {
      for (let j = i + 1; j < eventIntervals.length; j += 1) {
        const first = eventIntervals[i];
        const second = eventIntervals[j];
        const overlaps = first.interval.start < second.interval.end &&
          first.interval.end > second.interval.start;

        if (overlaps) {
          compactEventIds.add(first.event.id);
          compactEventIds.add(second.event.id);
        }
      }
    }

    return compactEventIds;
  };

  const getPointCollisionGroups = (sortedEvents) => {
    const groupedEvents = [];
    let currentGroup = null;

    sortedEvents.forEach(event => {
      const interval = getVisualInterval(event);
      if (!interval) return;

      if (!currentGroup || interval.start >= currentGroup.end) {
        currentGroup = {
          events: [],
          start: interval.start,
          end: interval.end
        };
        groupedEvents.push(currentGroup);
      }

      currentGroup.events.push(event);
      currentGroup.start = Math.min(currentGroup.start, interval.start);
      currentGroup.end = Math.max(currentGroup.end, interval.end);
    });

    return groupedEvents;
  };

  const assignPointLanes = (groupEvents, offsetForLane, compactEventIds) => {
    const placedRects = [];
    const assignments = new Map();
    let laneCount = 0;

    groupEvents.forEach(event => {
      const eventInterval = getVisualInterval(event);
      if (!eventInterval) return;
      const eventHeight = compactEventIds.has(event.id) ? COMPACT_EVENT_HEIGHT : EVENT_HEIGHT;
      const visualGap = 8;

      const collidesAtLane = (candidateLaneIndex) => placedRects.some(existingRect => {
        const yStart = offsetForLane(candidateLaneIndex, event);
        const yEnd = yStart + eventHeight;
        const xOverlap = eventInterval.start < existingRect.xEnd &&
          eventInterval.end > existingRect.xStart;
        const yOverlap = yStart < existingRect.yEnd + visualGap &&
          yEnd + visualGap > existingRect.yStart;

        return xOverlap && yOverlap;
      });

      let laneIndex = 0;
      while (collidesAtLane(laneIndex)) {
        laneIndex++;
      }

      const yStart = offsetForLane(laneIndex, event);
      placedRects.push({
        event,
        laneIndex,
        xStart: eventInterval.start,
        xEnd: eventInterval.end,
        yStart,
        yEnd: yStart + eventHeight
      });
      assignments.set(event.id, laneIndex);
      laneCount = Math.max(laneCount, laneIndex + 1);
    });

    return { assignments, laneCount };
  };

  const placePointGroups = (sortedEvents, offsetForLane, laneType, side, compactEventIds = new Set()) => {
    const groups = getPointCollisionGroups(sortedEvents);
    const availableSideHeight = Math.max(0, verticalSpace?.[side] ?? fallbackHalfHeight);

    groups.forEach(group => {
      const { assignments, laneCount } = assignPointLanes(group.events, offsetForLane, compactEventIds);
      const laneVisibility = [];
      const laneOffsets = [];
      const laneHeights = [];
      let lastVisibleLaneIndex = -1;
      const hiddenEvents = [];

      for (let laneIndex = 0; laneIndex < laneCount; laneIndex += 1) {
        const laneEvents = group.events.filter(event => assignments.get(event.id) === laneIndex);
        const maxLaneOffset = laneEvents.reduce((maxOffset, event) => (
          Math.max(maxOffset, offsetForLane(laneIndex, event))
        ), 0);
        const laneHeight = laneEvents.reduce((maxHeight, event) => (
          Math.max(maxHeight, compactEventIds.has(event.id) ? COMPACT_EVENT_HEIGHT : EVENT_HEIGHT)
        ), 0);
        const fitsViewport = BASE_DISTANCE + maxLaneOffset + laneHeight <= availableSideHeight - VIEWPORT_EDGE_PADDING;
        laneOffsets[laneIndex] = maxLaneOffset;
        laneHeights[laneIndex] = laneHeight;
        laneVisibility[laneIndex] = fitsViewport;

        if (fitsViewport) {
          lastVisibleLaneIndex = laneIndex;
        }
      }

      const shouldOverflow = lastVisibleLaneIndex < laneCount - 1;
      let firstHiddenLaneIndex = shouldOverflow ? Math.max(0, lastVisibleLaneIndex + 1) : -1;
      if (shouldOverflow) {
        const markerEdgePadding = side === 'top' ? VIEWPORT_EDGE_PADDING : OVERFLOW_MARKER_EDGE_PADDING;
        const maxMarkerOffsetForSide = Math.max(
          0,
          availableSideHeight - BASE_DISTANCE - OVERFLOW_MARKER_HEIGHT - markerEdgePadding
        );
        while (
          firstHiddenLaneIndex > 0 &&
          (side === 'top'
            ? maxMarkerOffsetForSide < (
                (laneOffsets[firstHiddenLaneIndex - 1] || 0) +
                (laneHeights[firstHiddenLaneIndex - 1] || 0)
              )
            : BASE_DISTANCE + (laneOffsets[firstHiddenLaneIndex] || 0) + OVERFLOW_MARKER_HEIGHT >
                availableSideHeight - markerEdgePadding)
        ) {
          firstHiddenLaneIndex -= 1;
        }
      }

      group.events.forEach(event => {
        const laneIndex = assignments.get(event.id);
        if (!Number.isInteger(laneIndex)) return;
        const eventInterval = getVisualInterval(event);

        if (!laneVisibility[laneIndex] || (shouldOverflow && laneIndex >= firstHiddenLaneIndex)) {
          hiddenEvents.push(event);
          offsets.set(event.id, {
            laneIndex,
            laneType,
            side,
            isOverflowed: true,
            useCompactEvent: compactEventIds.has(event.id),
            useCompactImage: compactEventIds.has(event.id)
          });
          return;
        }

        const verticalOffset = offsetForLane(laneIndex, event);
        const useCompactImage = compactEventIds.has(event.id);
        if (side === 'bottom' && eventInterval) {
          const visibleHeight = useCompactImage || !getEventImageUrl(event)
            ? EVENT_HEIGHT
            : 154;
          visibleBottomPointRects.push({
            xStart: eventInterval.start,
            xEnd: eventInterval.end,
            yStart: verticalOffset,
            yEnd: verticalOffset + visibleHeight
          });
        }

        offsets.set(event.id, {
          verticalOffset,
          laneIndex,
          laneType,
          side,
          isOverlapping: laneIndex > 0,
          useCompactEvent: compactEventIds.has(event.id),
          useCompactImage
        });
      });

      if (hiddenEvents.length > 0) {
        const hiddenPositions = hiddenEvents
          .map(event => {
            const eventDate = ensureDate(event.date)?.getTime();
            return Number.isFinite(eventDate)
              ? (eventDate - currentStartTime) * currentScale
              : null;
          })
          .filter(position => Number.isFinite(position));
        const positionPx = hiddenPositions.length > 0
          ? hiddenPositions.reduce((total, position) => total + position, 0) / hiddenPositions.length
          : (group.start + group.end) / 2;
        const groupVisibleStart = Math.max(group.start, 0);
        const groupVisibleEnd = Math.min(group.end, viewportWidth);
        const isGroupVisible = groupVisibleEnd > groupVisibleStart;
        if (!isGroupVisible) return;

        const representativeEvent = hiddenEvents.reduce((widestOffsetEvent, event) => (
          offsetForLane(0, event) > offsetForLane(0, widestOffsetEvent) ? event : widestOffsetEvent
        ), hiddenEvents[0]);
        const maxMarkerOffset = Math.max(
          0,
          availableSideHeight - BASE_DISTANCE - OVERFLOW_MARKER_HEIGHT -
            (side === 'top' ? VIEWPORT_EDGE_PADDING : OVERFLOW_MARKER_EDGE_PADDING)
        );
        const firstHiddenOffset = firstHiddenLaneIndex >= 0
          ? offsetForLane(firstHiddenLaneIndex, representativeEvent)
          : Math.min(...laneOffsets.filter(Number.isFinite));
        const desiredMarkerPositionPx = Math.max(
          (OVERFLOW_BUTTON_WIDTH / 2) + VIEWPORT_EDGE_PADDING,
          Math.min(viewportWidth - (OVERFLOW_BUTTON_WIDTH / 2) - VIEWPORT_EDGE_PADDING, positionPx)
        );
        const markerPositionPx = side === 'bottom'
          ? chooseBottomOverflowMarkerPosition({
              desiredPositionPx: desiredMarkerPositionPx,
              firstHiddenOffset,
              maxMarkerOffset
            })
          : desiredMarkerPositionPx;
        const markerRangeOffset = side === 'bottom'
          ? getBottomRangeOffsetForMarkerPosition(markerPositionPx)
          : 0;
        const markerOffset = Math.min(
          Math.max(firstHiddenOffset, markerRangeOffset),
          maxMarkerOffset
        );

        overflowGroups.push({
          id: `${side}-${overflowGroups.length}`,
          side,
          events: hiddenEvents,
          positionPx: markerPositionPx,
          eventPositions: hiddenEvents
            .map(event => ({
              id: event.id,
              positionPx: (ensureDate(event.date)?.getTime() - currentStartTime) * currentScale
            }))
            .filter(({ positionPx }) => Number.isFinite(positionPx) && positionPx >= 0 && positionPx <= viewportWidth),
          verticalOffset: markerOffset,
          laneIndex: Math.max(0, firstHiddenLaneIndex)
        });
      }
    });
  };

  const sortByDate = (a, b) => {
    const dateA = ensureDate(a.date)?.getTime() || 0;
    const dateB = ensureDate(b.date)?.getTime() || 0;
    return dateA - dateB;
  };

  const placeRanges = (sortedRanges) => {
    const rangeRows = [];

    sortedRanges.forEach(event => {
      const metrics = getRangeMetrics(event);
      if (!metrics) return;

      let rowIndex = 0;
      while ((rangeRows[rowIndex] || []).some(existing => {
        const existingMetrics = getRangeMetrics(existing);
        if (!existingMetrics) return false;
        return metrics.interval.start < existingMetrics.interval.end &&
          metrics.interval.end > existingMetrics.interval.start;
      })) {
        rowIndex++;
      }

      if (!rangeRows[rowIndex]) rangeRows[rowIndex] = [];
      rangeRows[rowIndex].push(event);

      offsets.set(event.id, {
        rangeOffset: rowIndex * RANGE_ROW_SPACING,
        rangeLeft: metrics.rangeLeft,
        rangeWidth: metrics.rangeWidth,
        laneIndex: rowIndex,
        laneType: 'range',
        isOverlapping: rowIndex > 0
      });
    });

    const availableBottomHeight = Math.max(0, verticalSpace?.bottom ?? fallbackHalfHeight);
    const firstRowThatExceedsViewport = rangeRows.findIndex((row, rowIndex) => {
      if (!row?.length) return false;
      return RANGE_BAND_DISTANCE + (rowIndex * RANGE_ROW_SPACING) + RANGE_SPAN_HEIGHT >
        availableBottomHeight - VIEWPORT_EDGE_PADDING;
    });

    if (firstRowThatExceedsViewport === -1) {
      return rangeRows;
    }

    const maxMarkerRangeOffset = Math.max(
      0,
      availableBottomHeight - RANGE_BAND_DISTANCE - OVERFLOW_MARKER_HEIGHT - VIEWPORT_EDGE_PADDING
    );
    let firstHiddenRowIndex = firstRowThatExceedsViewport;
    while (
      firstHiddenRowIndex > 0 &&
      firstHiddenRowIndex * RANGE_ROW_SPACING > maxMarkerRangeOffset
    ) {
      firstHiddenRowIndex -= 1;
    }

    const hiddenEvents = rangeRows
      .slice(firstHiddenRowIndex)
      .flat()
      .filter(event => {
        const metrics = getRangeMetrics(event);
        if (!metrics) return false;
        const visibleStart = Math.max(0, metrics.rangeLeft);
        const visibleEnd = Math.min(viewportWidth, metrics.rangeRight);
        return metrics.rangeWidth === 0
          ? metrics.rangeLeft >= 0 && metrics.rangeLeft <= viewportWidth
          : visibleEnd > visibleStart;
      });

    rangeRows.slice(firstHiddenRowIndex).flat().forEach(event => {
      const existingOffset = offsets.get(event.id);
      if (!existingOffset) return;
      offsets.set(event.id, {
        ...existingOffset,
        isOverflowed: true
      });
    });

    if (hiddenEvents.length > 0) {
      const hiddenPositions = hiddenEvents
        .map(event => {
          const metrics = getRangeMetrics(event);
          if (!metrics) return null;
          const visibleStart = Math.max(0, metrics.rangeLeft);
          const visibleEnd = Math.min(viewportWidth, metrics.rangeRight);
          if (metrics.rangeWidth === 0) return metrics.rangeLeft;
          return (visibleStart + visibleEnd) / 2;
        })
        .filter(position => Number.isFinite(position));
      const positionPx = hiddenPositions.length > 0
        ? hiddenPositions.reduce((total, position) => total + position, 0) / hiddenPositions.length
        : viewportWidth / 2;
      const markerPositionPx = Math.max(
        (OVERFLOW_BUTTON_WIDTH / 2) + VIEWPORT_EDGE_PADDING,
        Math.min(viewportWidth - (OVERFLOW_BUTTON_WIDTH / 2) - VIEWPORT_EDGE_PADDING, positionPx)
      );

      overflowGroups.push({
        id: `range-${overflowGroups.length}`,
        side: 'bottom',
        laneType: 'range',
        events: hiddenEvents,
        positionPx: markerPositionPx,
        eventPositions: hiddenEvents
          .map(event => {
            const metrics = getRangeMetrics(event);
            if (!metrics) return null;
            const visibleStart = Math.max(0, metrics.rangeLeft);
            const visibleEnd = Math.min(viewportWidth, metrics.rangeRight);
            const positionPx = metrics.rangeWidth === 0
              ? metrics.rangeLeft
              : (visibleStart + visibleEnd) / 2;
            return { id: event.id, positionPx };
          })
          .filter(eventPosition => (
            eventPosition &&
            Number.isFinite(eventPosition.positionPx) &&
            eventPosition.positionPx >= 0 &&
            eventPosition.positionPx <= viewportWidth
          )),
        verticalOffset: firstHiddenRowIndex * RANGE_ROW_SPACING,
        laneIndex: firstHiddenRowIndex
      });
    }

    return rangeRows.slice(0, firstHiddenRowIndex);
  };

  const sortedRanges = [...rangeEvents].sort(sortByDate);
  const visibleRangeRows = placeRanges(sortedRanges);

  const getOverlappingRangeRowCountForInterval = (eventInterval) => {
    return visibleRangeRows.reduce((overlappingRowCount, row, rowIndex) => {
      const overlapsRow = row.some(rangeEvent => {
        const rangeMetrics = getRangeMetrics(rangeEvent);
        if (!rangeMetrics) return false;

        return eventInterval.start < rangeMetrics.interval.end &&
          eventInterval.end > rangeMetrics.interval.start;
      });

      return overlapsRow ? Math.max(overlappingRowCount, rowIndex + 1) : overlappingRowCount;
    }, 0);
  };

  const getBottomRangeOffsetForInterval = (eventInterval) => {
    if (!eventInterval) return 0;

    const overlappingRangeRowCount = getOverlappingRangeRowCountForInterval(eventInterval);
    if (overlappingRangeRowCount === 0) return 0;

    const rangeClearance = 14;
    const rangeBandBottom = RANGE_BAND_DISTANCE +
      (overlappingRangeRowCount * RANGE_ROW_SPACING) +
      rangeClearance;

    return Math.max(0, rangeBandBottom - BASE_DISTANCE);
  };

  const getBottomRangeOffsetForMarkerPosition = (positionPx) => {
    return getBottomRangeOffsetForInterval({
      start: positionPx - (OVERFLOW_BUTTON_WIDTH / 2) - (EVENT_PADDING / 2),
      end: positionPx + (OVERFLOW_BUTTON_WIDTH / 2) + (EVENT_PADDING / 2)
    });
  };

  const chooseBottomOverflowMarkerPosition = ({
    desiredPositionPx,
    firstHiddenOffset,
    maxMarkerOffset
  }) => {
    const markerHalfWidth = (OVERFLOW_BUTTON_WIDTH / 2) + (EVENT_PADDING / 2);
    const minPosition = (OVERFLOW_BUTTON_WIDTH / 2) + VIEWPORT_EDGE_PADDING;
    const maxPosition = viewportWidth - (OVERFLOW_BUTTON_WIDTH / 2) - VIEWPORT_EDGE_PADDING;
    const searchMin = minPosition;
    const searchMax = maxPosition;
    const clampCandidate = (positionPx) => Math.max(searchMin, Math.min(searchMax, positionPx));
    const candidates = new Set([
      clampCandidate(desiredPositionPx),
      searchMin,
      searchMax
    ]);

    visibleRangeRows.flat().forEach(rangeEvent => {
      const rangeMetrics = getRangeMetrics(rangeEvent);
      if (!rangeMetrics) return;
      candidates.add(clampCandidate(rangeMetrics.interval.start - markerHalfWidth));
      candidates.add(clampCandidate(rangeMetrics.interval.end + markerHalfWidth));
    });

    visibleBottomPointRects.forEach(rect => {
      candidates.add(clampCandidate(rect.xStart - markerHalfWidth));
      candidates.add(clampCandidate(rect.xEnd + markerHalfWidth));
    });

    const getCandidateOffset = (positionPx) => Math.min(
      Math.max(firstHiddenOffset, getBottomRangeOffsetForMarkerPosition(positionPx)),
      maxMarkerOffset
    );

    const getPointOverlapScore = (positionPx) => {
      const markerRect = {
        xStart: positionPx - markerHalfWidth,
        xEnd: positionPx + markerHalfWidth,
        yStart: getCandidateOffset(positionPx),
        yEnd: getCandidateOffset(positionPx) + OVERFLOW_MARKER_HEIGHT
      };

      return visibleBottomPointRects.reduce((score, rect) => {
        const xOverlap = markerRect.xStart < rect.xEnd && markerRect.xEnd > rect.xStart;
        const yOverlap = markerRect.yStart < rect.yEnd && markerRect.yEnd > rect.yStart;
        return xOverlap && yOverlap ? score + 1 : score;
      }, 0);
    };

    return Array.from(candidates).reduce((bestPosition, candidatePosition) => {
      const bestOffset = getBottomRangeOffsetForMarkerPosition(bestPosition);
      const candidateOffset = getBottomRangeOffsetForMarkerPosition(candidatePosition);
      const bestOverflow = Math.max(0, bestOffset - maxMarkerOffset);
      const candidateOverflow = Math.max(0, candidateOffset - maxMarkerOffset);
      const bestPointOverlap = getPointOverlapScore(bestPosition);
      const candidatePointOverlap = getPointOverlapScore(candidatePosition);
      const bestDistance = Math.abs(bestPosition - desiredPositionPx);
      const candidateDistance = Math.abs(candidatePosition - desiredPositionPx);

      if (candidatePointOverlap !== bestPointOverlap) {
        return candidatePointOverlap < bestPointOverlap ? candidatePosition : bestPosition;
      }

      if (candidateOverflow !== bestOverflow) {
        return candidateOverflow < bestOverflow ? candidatePosition : bestPosition;
      }

      if (candidateDistance !== bestDistance) {
        return candidateDistance < bestDistance ? candidatePosition : bestPosition;
      }

      if (candidateOffset !== bestOffset) {
        return candidateOffset < bestOffset ? candidatePosition : bestPosition;
      }

      return bestPosition;
    }, clampCandidate(desiredPositionPx));
  };

  const getBottomPointRangeOffset = (event) => {
    return getBottomRangeOffsetForInterval(getVisualInterval(event));
  };

  const placeSideEvents = (sideEvents, extraOffset = 0) => {
    const points = [...sideEvents].sort(sortByDate);
    const compactImageEventIds = getCollisionGroupIds(points);
    const getExtraOffset = typeof extraOffset === 'function'
      ? extraOffset
      : () => extraOffset;

    placePointGroups(
      points,
      (laneIndex, event) => getExtraOffset(event) + (laneIndex * POINT_LANE_SPACING),
      'point',
      sideEvents === bottomEvents ? 'bottom' : 'top',
      compactImageEventIds
    );
  };

  placeSideEvents(topEvents);
  placeSideEvents(bottomEvents, getBottomPointRangeOffset);

  return { offsets, overflowGroups };
};
